import {
  Component,
  input,
  output,
  ViewChild,
  ElementRef,
  forwardRef,
  AfterViewInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CommonModule, NgClass } from '@angular/common';
import { InputLabelComponent } from 'src/app/ui-kit/form-controls/input-label/input-label.component';

/**
 * Custom Textarea Input Component
 *
 * A reusable textarea component that implements Angular's ControlValueAccessor interface
 * for seamless integration with Angular Forms (both Template-driven and Reactive).
 *
 * This component provides consistent styling and behavior with the app-input component
 * but uses a textarea element for multi-line text input.
 *
 * @example
 * // Template-driven form
 * <app-textarea-input
 *   label="Description"
 *   placeholder="Enter description"
 *   [(ngModel)]="description"
 * ></app-textarea-input>
 *
 * @example
 * // Reactive form
 * <app-textarea-input
 *   label="Notes"
 *   [formControl]="notesControl"
 *   [rows]="5"
 *   helperText="Enter any additional notes"
 * ></app-textarea-input>
 */
@Component({
  selector: 'app-textarea-input',
  templateUrl: './textarea-input.component.html',
  styleUrls: ['./textarea-input.component.scss'],
  standalone: true,
  imports: [CommonModule, NgClass, InputLabelComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaInputComponent),
      multi: true,
    },
  ],
})
export class TextareaInputComponent implements ControlValueAccessor, AfterViewInit {
  /**
   * Reference to the underlying native textarea element
   * Used to access native textarea methods like focus()
   */
  @ViewChild('textareaElementRef', { static: false }) textareaElementRef!: ElementRef<HTMLTextAreaElement>;

  // ========================================
  // Custom Styling
  // ========================================

  /**
   * Enable theme shadow on textarea
   * @default false
   */
  readonly shadow = input<boolean>(false);

  /**
   * Enable theme border on textarea
   * @default true
   */
  readonly border = input<boolean>(true);

  /**
   * Custom CSS class to apply
   */
  readonly customClass = input<string | undefined>(undefined);

  /**
   * Unique ID for the textarea element
   */
  readonly id = input<string>(`textarea-${Math.random().toString(36).substr(2, 9)}`);

  // ========================================
  // Text Capitalization & Correction
  // ========================================

  /**
   * Controls automatic capitalization of text as user types
   * - 'off': No autocapitalization
   * - 'none': No autocapitalization (same as 'off')
   * - 'on': Capitalize each word's first letter
   * - 'sentences': Capitalize first letter of each sentence
   * - 'words': Capitalize first letter of each word
   * - 'characters': Capitalize all characters
   * @default 'sentences'
   */
  readonly autocapitalize = input<string>('sentences');

  /**
   * Enable autocorrect/spell check
   * @default 'on'
   */
  readonly autocorrect = input<string>('on');

  /**
   * Enable spellcheck
   * @default true
   */
  readonly spellcheck = input<boolean>(true);

  // ========================================
  // Label & Helper Text
  // ========================================

  /**
   * Label text displayed above the textarea
   */
  readonly label = input<string | undefined>(undefined);

  /**
   * Description text shown directly under the label (muted, small).
   * Mirrors the `description` slot on app-input.
   */
  readonly description = input<string | undefined>(undefined);

  /**
   * Helper text displayed below the textarea
   * Useful for providing additional guidance
   */
  readonly helperText = input<string | undefined>(undefined);

  /**
   * Error message text displayed below the textarea
   * Takes precedence over helper text when present
   */
  readonly errorText = input<string | undefined>(undefined);

  // ========================================
  // Textarea Behavior
  // ========================================

  /**
   * Placeholder text shown when textarea is empty
   */
  readonly placeholder = input<string | undefined>(undefined);

  /**
   * Number of visible text rows
   * @default 3
   */
  readonly rows = input<number>(3);

  /**
   * Minimum number of rows
   */
  readonly minRows = input<number | undefined>(undefined);

  /**
   * Maximum number of rows (for auto-resize)
   */
  readonly maxRows = input<number | undefined>(undefined);

  /**
   * Enable automatic height adjustment
   * @default false
   */
  readonly autoResize = input<boolean>(false);

  /**
   * Disable the textarea
   * @default false
   */
  disabled: boolean = false;

  /**
   * Make the textarea readonly
   * @default false
   */
  readonly readonly = input<boolean>(false);

  /**
   * Mark the textarea as required
   * @default false
   */
  readonly required = input<boolean>(false);

  /**
   * Maximum length of text (character limit)
   */
  readonly maxlength = input<number | undefined>(undefined);

  /**
   * Minimum length of text
   */
  readonly minlength = input<number | undefined>(undefined);

  /**
   * Name attribute for the textarea
   */
  readonly name = input<string | undefined>(undefined);

  // ========================================
  // Events
  // ========================================

  /**
   * Emitted when the textarea value changes
   */
  readonly valueChange = output<string>();

  /**
   * Emitted when the textarea gains focus
   */
  readonly focused = output<FocusEvent>();

  /**
   * Emitted when the textarea loses focus
   */
  readonly blurred = output<FocusEvent>();

  /**
   * Emitted when Enter key is pressed (with Ctrl/Cmd)
   */
  readonly enterPressed = output<void>();

  // ========================================
  // ControlValueAccessor Properties
  // ========================================

  /**
   * Current value of the textarea
   */
  value: string = '';

  /**
   * Callback function to call when value changes
   * Part of ControlValueAccessor interface
   */
  private onChange: (value: string) => void = () => {};

  /**
   * Callback function to call when textarea is touched
   * Part of ControlValueAccessor interface
   */
  private onTouched: () => void = () => {};

  // ========================================
  // Lifecycle Hooks
  // ========================================

  ngAfterViewInit(): void {
    // Focus the textarea if autofocus is needed
    if (this.textareaElementRef) {
      // Can add autofocus logic here if needed
    }
  }

  // ========================================
  // ControlValueAccessor Methods
  // ========================================

  /**
   * Write a new value to the element
   * Called by Angular Forms when programmatically setting the value
   */
  writeValue(value: string): void {
    this.value = value || '';
  }

  /**
   * Register a callback function that is called when the value changes
   * Called by Angular Forms to set up the change handler
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Register a callback function that is called when the element is touched
   * Called by Angular Forms to set up the touch handler
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Set the disabled state of the element
   * Called by Angular Forms when the disabled state changes
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ========================================
  // Event Handlers
  // ========================================

  /**
   * Handle textarea input changes
   */
  onTextareaInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);

    if (this.autoResize()) {
      this.adjustHeight();
    }
  }

  /**
   * Handle textarea focus event
   */
  onFocusChange(event: FocusEvent): void {
    this.focused.emit(event);
  }

  /**
   * Handle textarea blur event
   */
  onBlurChange(event: FocusEvent): void {
    this.onTouched();
    this.blurred.emit(event);
  }

  /**
   * Handle key press events
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      this.enterPressed.emit();
    }
  }

  // ========================================
  // Public Methods
  // ========================================

  /**
   * Programmatically focus the textarea
   */
  setFocus(): void {
    if (this.textareaElementRef) {
      this.textareaElementRef.nativeElement.focus();
    }
  }

  /**
   * Programmatically blur the textarea
   */
  setBlur(): void {
    if (this.textareaElementRef) {
      this.textareaElementRef.nativeElement.blur();
    }
  }

  /**
   * Get the native textarea element
   */
  getTextareaElement(): HTMLTextAreaElement | null {
    return this.textareaElementRef ? this.textareaElementRef.nativeElement : null;
  }

  /**
   * Adjust textarea height based on content
   */
  private adjustHeight(): void {
    if (!this.textareaElementRef) return;

    const textarea = this.textareaElementRef.nativeElement;
    textarea.style.height = 'auto';
    
    let newHeight = textarea.scrollHeight;

    if (this.minRows()) {
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
      const minHeight = this.minRows()! * lineHeight;
      newHeight = Math.max(newHeight, minHeight);
    }

    if (this.maxRows()) {
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
      const maxHeight = this.maxRows()! * lineHeight;
      newHeight = Math.min(newHeight, maxHeight);
    }

    textarea.style.height = `${newHeight}px`;
  }

  /**
   * Get computed dynamic type based on password visibility
   */
  get type(): string {
    return 'text';
  }
}

